import { cn } from "@repo/shared/utils";
import { ChevronDownCircle, ChevronUpCircle } from "lucide-react";
import { useId, useState } from "react";

export type CollapsiblePanelProps = {
  id?: string;
  idSuffix?: string;
  children?: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onToggle?: (open: boolean) => void;
  className?: string;
  contentClassName?: string;
  title?: string;
};

export const CollapsiblePanel = ({
  id,
  idSuffix,
  children,
  defaultOpen,
  open,
  onToggle,
  className,
  contentClassName,
  title,
}: CollapsiblePanelProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen ?? true);
  const isExternalOpen = open !== undefined;
  const isOpen = isExternalOpen ? open : internalIsOpen;
  const panelId = useId();
  const contentId = `${panelId}-content`;

  const handleToggle = () => {
    if (isExternalOpen) {
      onToggle?.(!open);
    } else {
      setInternalIsOpen(!internalIsOpen);
      onToggle?.(!internalIsOpen);
    }
  };
  return (
    <section
      id={id && idSuffix && idSuffix.length > 0 ? `${id}${idSuffix}` : id}
      className={cn("border-x border-t last:border-b", className)}
      data-open={isOpen}
    >
      <button
        type="button"
        aria-controls={contentId}
        aria-expanded={isOpen}
        onClick={handleToggle}
        className={cn(
          "w-full select-none",
          "flex items-center justify-between gap-3",
          "px-4 py-3 text-left",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        )}
        aria-label={title ? undefined : "Toggle section"}
      >
        <span className="flex-1 font-semibold leading-none tracking-tight">
          {title}
        </span>
        {isOpen ? (
          <ChevronUpCircle className="h-5 w-5 block" />
        ) : (
          <ChevronDownCircle className="h-5 w-5 block" />
        )}
      </button>

      <div
        id={contentId}
        role="region"
        aria-labelledby={panelId}
        className={cn(
          "grid transition-all duration-300 ease-in-out",
          "grid-rows-[0fr] data-[open=true]:grid-rows-[1fr]",
          "px-4",
          isOpen ? "data-[open=true]:pb-3" : "pb-0"
        )}
        data-open={isOpen}
      >
        <div className={cn("min-h-0 overflow-hidden", contentClassName)}>
          {children}
        </div>
      </div>
    </section>
  );
};
